import type { EpisodeQueuePageStatus } from './comicEpisodeGenerateQueue'

/** 与时间线轨 {@link timelineRoundAccent}（`createPageTimelinePresentation`）同系的语义色键 */
export type EpisodeQueueRailSemantic = 'success' | 'warn' | 'danger' | 'muted' | 'cancelled'

/** 灵动岛阶段 → 时间线语义色 */
export type EpisodeQueueIslandPhaseSemantic =
  | 'queued'
  | 'running'
  | 'awaiting'
  | 'success'
  | 'warning'
  | 'cancelled'

const RAIL_VAR: Record<EpisodeQueueRailSemantic, string> = {
  success: 'var(--timeline-rail-success)',
  warn: 'var(--timeline-rail-warn)',
  danger: 'var(--timeline-rail-danger)',
  muted: 'var(--timeline-rail-muted)',
  cancelled: 'var(--timeline-rail-cancelled)',
}

function railMix(
  semantic: EpisodeQueueRailSemantic,
  kind: 'dotBorder' | 'dotBg' | 'stem' | 'cardBorder' | 'cardBg' | 'label' | 'chipBorder' | 'chipBg' | 'chipText',
): string {
  const r = RAIL_VAR[semantic]
  switch (kind) {
    case 'dotBorder':
      return `border-[color-mix(in_srgb,${r}_58%,var(--line)_42%)]`
    case 'dotBg':
      return `bg-[color-mix(in_srgb,${r}_24%,var(--panel)_76%)]`
    case 'stem':
      return `bg-[${r}]`
    case 'cardBorder':
      return `border-[color-mix(in_srgb,${r}_55%,var(--line)_45%)]`
    case 'cardBg':
      return `bg-[color-mix(in_srgb,${r}_9%,var(--panel)_91%)]`
    case 'label':
      return `text-[color-mix(in_srgb,${r}_58%,var(--ink)_42%)]`
    case 'chipBorder':
      return `border-[color-mix(in_srgb,${r}_34%,var(--line)_66%)]`
    case 'chipBg':
      return `bg-[color-mix(in_srgb,${r}_11%,var(--panel)_89%)]`
    case 'chipText':
      return `text-[color-mix(in_srgb,${r}_58%,var(--ink)_42%)]`
  }
}

export function episodeQueuePageRailSemantic(status: EpisodeQueuePageStatus): EpisodeQueueRailSemantic {
  switch (status) {
    case 'done':
      return 'success'
    case 'failed':
      return 'danger'
    case 'cancelled':
      return 'cancelled'
    case 'running':
    case 'awaiting_selection':
      return 'warn'
    default:
      return 'muted'
  }
}

export function episodeQueueIslandPhaseRailSemantic(
  phase: EpisodeQueueIslandPhaseSemantic,
): EpisodeQueueRailSemantic {
  switch (phase) {
    case 'success':
      return 'success'
    case 'warning':
      return 'danger'
    case 'cancelled':
      return 'cancelled'
    case 'running':
    case 'awaiting':
      return 'warn'
    case 'queued':
      return 'muted'
  }
}

/** 队列步骤圆点（对齐时间线节点描边/底） */
export function episodeQueueStepDotClass(status: EpisodeQueuePageStatus, angular: boolean): string {
  const round = angular ? 'rounded-none' : 'rounded-full'
  const semantic = episodeQueuePageRailSemantic(status)

  if (status === 'awaiting_selection') {
    return [
      'flex h-7 w-7 shrink-0 items-center justify-center border-2 border-dashed',
      round,
      railMix('warn', 'dotBorder'),
      railMix('warn', 'dotBg'),
    ].join(' ')
  }

  return [
    'flex h-7 w-7 shrink-0 items-center justify-center border',
    round,
    railMix(semantic, 'dotBorder'),
    railMix(semantic, 'dotBg'),
  ].join(' ')
}

export function episodeQueueStepStemClass(status: EpisodeQueuePageStatus): string {
  return `w-px ${railMix(episodeQueuePageRailSemantic(status), 'stem')}`
}

export function episodeQueueStepCardClass(status: EpisodeQueuePageStatus, isCurrent: boolean): string {
  const semantic = isCurrent ? 'warn' : episodeQueuePageRailSemantic(status)
  return [
    railMix(semantic, 'cardBorder'),
    isCurrent
      ? railMix('warn', 'cardBg')
      : 'bg-[color-mix(in_srgb,var(--panel)_94%,var(--surface-muted)_6%)]',
  ].join(' ')
}

export function episodeQueueStatusChipClass(status: EpisodeQueuePageStatus): string {
  const semantic = episodeQueuePageRailSemantic(status)
  return [
    railMix(semantic, 'chipBorder'),
    railMix(semantic, 'chipBg'),
    railMix(semantic, 'chipText'),
  ].join(' ')
}

export function episodeQueueIslandPhaseDotClass(phase: EpisodeQueueIslandPhaseSemantic): string {
  const semantic = episodeQueueIslandPhaseRailSemantic(phase)
  const base = 'inline-block h-1.5 w-1.5 shrink-0 rounded-full'
  const fill = railMix(semantic, 'stem')
  const pulse = phase === 'running' ? ' animate-pulse' : ''
  return `${base} ${fill}${pulse}`
}

export function episodeQueueProgressTrackClass(angular: boolean): string {
  return [
    'h-1.5 overflow-hidden',
    'bg-[color-mix(in_srgb,var(--timeline-rail-muted)_18%,var(--surface-muted)_82%)]',
    angular ? 'rounded-none' : 'rounded-[var(--radius-sm)]',
  ].join(' ')
}

export function episodeQueueProgressFillClass(opts: {
  cancelled: boolean
  finished: boolean
  hasFailed: boolean
  inProgress: boolean
}): string {
  if (opts.cancelled) return railMix('cancelled', 'stem')
  if (opts.finished && opts.hasFailed) return railMix('warn', 'stem')
  if (opts.finished) return railMix('success', 'stem')
  if (opts.inProgress) return railMix('warn', 'stem')
  return railMix('muted', 'stem')
}

export function episodeQueueSummaryStatClass(kind: 'default' | 'success' | 'failed' | 'awaiting'): string {
  switch (kind) {
    case 'success':
      return railMix('success', 'label')
    case 'failed':
      return railMix('danger', 'label')
    case 'awaiting':
      return railMix('warn', 'label')
    default:
      return 'text-ink-soft'
  }
}

export function episodeQueueCurrentBadgeClass(): string {
  return railMix('warn', 'label')
}
